import React, { useEffect, useRef, useState } from 'react';
import { Check, Clock, MessageSquare, Send, Trash2 } from 'lucide-react';
import { fmtTc } from '../lib/produzione';

/**
 * Player di revisione con commenti al timecode (stile Frame.io), usato sia
 * internamente (PedContenuto) sia nel portale cliente.
 *
 * Props:
 *  - media: { url, video, thumb }
 *  - commenti: [{ id, timecode_sec, testo, autore, autore_nome, risolto, origine, creato_il, mio }]
 *  - onAdd(testo, timecode_sec|null) → Promise
 *  - onToggleRisolto(c) (opzionale: se assente non si mostra la spunta)
 *  - onDelete(c) (opzionale: mostrato solo sui commenti con `mio` o se canDeleteAll)
 *  - placeholder
 */
export default function ReviewPlayer({ media, commenti = [], onAdd, onToggleRisolto, onDelete, canDeleteAll = false,
  placeholder = 'Scrivi un commento…', compact = false }) {
  const videoRef = useRef(null);
  const [durata, setDurata] = useState(0);
  const [now, setNow] = useState(0);
  const [testo, setTesto] = useState('');
  const [alTc, setAlTc] = useState(true);
  const [tcFissato, setTcFissato] = useState(null);   // timecode catturato quando inizi a scrivere
  const [busy, setBusy] = useState(false);
  const [filtro, setFiltro] = useState('aperti');     // aperti | tutti

  const mediaUrl = media ? media.url : null;
  useEffect(() => { setDurata(0); setNow(0); setTcFissato(null); }, [mediaUrl]);

  const isVideo = !!(media && media.video);
  const seek = (t) => {
    const v = videoRef.current;
    if (!v || t == null) return;
    v.currentTime = Number(t);
    v.pause();
  };

  const onFocusInput = () => {
    const v = videoRef.current;
    if (isVideo && v) { v.pause(); setTcFissato(v.currentTime); }
  };

  const invia = async () => {
    const t = testo.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const tc = isVideo && alTc ? (tcFissato ?? videoRef.current?.currentTime ?? null) : null;
      await onAdd(t, tc);
      setTesto(''); setTcFissato(null);
    } catch { /* toast globale */ }
    setBusy(false);
  };

  const lista = [...commenti]
    .filter(c => filtro === 'tutti' || !c.risolto || !onToggleRisolto)
    .sort((a, b) => (a.timecode_sec ?? -1) - (b.timecode_sec ?? -1) || new Date(a.creato_il) - new Date(b.creato_il));
  const aperti = commenti.filter(c => !c.risolto).length;
  const conTc = commenti.filter(c => c.timecode_sec != null);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: 14 }} className="eden-review">
      {/* ── Player ── */}
      <div>
        <div style={{ background: '#000', borderRadius: 10, overflow: 'hidden', display: 'flex', justifyContent: 'center', maxHeight: compact ? 420 : 520 }}>
          {!media ? (
            <div style={{ padding: 40, color: 'var(--text-dim)', fontSize: 13 }}>Nessun media</div>
          ) : isVideo ? (
            <video ref={videoRef} src={media.url} poster={media.thumb || undefined} controls playsInline preload="metadata"
              onLoadedMetadata={e => setDurata(e.currentTarget.duration || 0)}
              onTimeUpdate={e => setNow(e.currentTarget.currentTime)}
              style={{ maxWidth: '100%', maxHeight: compact ? 420 : 520, display: 'block' }} />
          ) : (
            <img src={media.url} alt="" style={{ maxWidth: '100%', maxHeight: compact ? 420 : 520, objectFit: 'contain', display: 'block' }} />
          )}
        </div>
        {/* timeline con i marker dei commenti */}
        {isVideo && durata > 0 && (
          <div style={{ position: 'relative', height: 18, marginTop: 8 }}>
            <div style={{ position: 'absolute', top: 8, left: 0, right: 0, height: 3, background: 'var(--bg3)', borderRadius: 2 }} />
            <div style={{ position: 'absolute', top: 8, left: 0, width: `${Math.min(100, (now / durata) * 100)}%`, height: 3, background: 'var(--cyan)', borderRadius: 2 }} />
            {conTc.map(c => (
              <button key={c.id} onClick={() => seek(c.timecode_sec)} title={`${fmtTc(c.timecode_sec)} — ${c.testo}`}
                style={{ position: 'absolute', top: 2, left: `calc(${Math.min(100, (c.timecode_sec / durata) * 100)}% - 6px)`,
                  width: 12, height: 14, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 3,
                  background: c.risolto ? 'var(--green)' : c.origine === 'cliente' ? 'var(--yellow)' : 'var(--purple)' }} />
            ))}
          </div>
        )}
        {/* nuovo commento */}
        {onAdd && (
          <div style={{ marginTop: 10 }}>
            <textarea className="eden-input" value={testo} onChange={e => setTesto(e.target.value)} onFocus={onFocusInput}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) invia(); }}
              placeholder={placeholder} style={{ minHeight: 64, resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
              {isVideo && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={alTc} onChange={e => setAlTc(e.target.checked)} style={{ accentColor: 'var(--cyan)' }} />
                  <Clock size={12} /> al secondo {fmtTc(tcFissato ?? now)}
                </label>
              )}
              <button onClick={invia} disabled={busy || !testo.trim()} className="eden-btn-primary"
                style={{ marginLeft: 'auto', fontSize: 12, opacity: busy || !testo.trim() ? 0.5 : 1 }}>
                <Send size={13} /> Commenta
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Lista commenti ── */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <MessageSquare size={14} color="var(--text-muted)" />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>Commenti</span>
          <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{onToggleRisolto ? `${aperti} aperti · ${commenti.length} totali` : commenti.length}</span>
          {onToggleRisolto && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 3, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: 2 }}>
              {[['aperti', 'Aperti'], ['tutti', 'Tutti']].map(([k, l]) => (
                <button key={k} onClick={() => setFiltro(k)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, border: 'none', cursor: 'pointer',
                  background: filtro === k ? 'var(--cyan-dim)' : 'transparent', color: filtro === k ? 'var(--cyan)' : 'var(--text-muted)' }}>{l}</button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: compact ? 320 : 560, overflowY: 'auto' }}>
          {lista.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--text-dim)', padding: '14px 4px' }}>
              {commenti.length ? 'Tutti i commenti sono risolti.' : 'Nessun commento. Metti in pausa e scrivi: il commento resta agganciato a quel secondo.'}
            </div>
          )}
          {lista.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 8, padding: '8px 10px', borderRadius: 9, background: 'var(--surface)',
              border: `1px solid ${c.origine === 'cliente' ? 'rgba(245,158,11,0.35)' : 'var(--border)'}`, opacity: c.risolto ? 0.6 : 1 }}>
              {onToggleRisolto && (
                <button onClick={() => onToggleRisolto(c)} title={c.risolto ? 'Riapri' : 'Segna come fatto'}
                  style={{ width: 18, height: 18, minWidth: 18, marginTop: 1, padding: 0, borderRadius: 5, cursor: 'pointer',
                    border: c.risolto ? 'none' : '1.5px solid var(--border-h, var(--border))', background: c.risolto ? 'var(--green)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {c.risolto && <Check size={12} color="var(--on-accent)" />}
                </button>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {c.timecode_sec != null && (
                    <button onClick={() => seek(c.timecode_sec)} style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, padding: '0 6px', borderRadius: 5,
                      border: 'none', cursor: isVideo ? 'pointer' : 'default', background: 'var(--cyan-dim)', color: 'var(--cyan)' }}>{fmtTc(c.timecode_sec)}</button>
                  )}
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text)' }}>{c.autore || c.autore_nome || 'Cliente'}</span>
                  {c.origine === 'cliente' && onToggleRisolto && <span style={{ fontSize: 10, color: 'var(--yellow)', fontWeight: 700 }}>CLIENTE</span>}
                  {c.versione_numero && <span style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>v{c.versione_numero}</span>}
                  <span style={{ fontSize: 10.5, color: 'var(--text-dim)', marginLeft: 'auto' }}>
                    {c.creato_il ? new Date(c.creato_il).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, whiteSpace: 'pre-wrap', textDecoration: c.risolto ? 'line-through' : 'none' }}>{c.testo}</div>
              </div>
              {onDelete && (c.mio || canDeleteAll) && (
                <button onClick={() => onDelete(c)} title="Elimina" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, alignSelf: 'flex-start' }}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
